import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import ArticleCategory from '../../../smoothr-web-app-core/models/ArticleCategory';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import Article from '../../../smoothr-web-app-core/models/Article';
import dayjs from 'dayjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { TimeUtils } from '../../../smoothr-web-app-core/utils/time-utils';
import { OrderType } from '../../../smoothr-web-app-core/enums/OrderType';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { environment } from '../../../environments/environment';
import { axiosErrorToMessage, getCustomAvailability, numberToCurrency, sleep } from '../../../smoothr-web-app-core/utils/utils';
import { NavigationService } from 'src/app/services/navigation.service';
import { BehaviorSubject } from 'rxjs';
import { TOGGLE_TAG } from 'src/app/components/menu-filters/menu-filters.component';
import { ArticleCategoryType } from 'src/smoothr-web-app-core/enums/ArticleCategoryType';
import { addToBasket, openArticleEditModal, removeBasket, toggleBasket } from '../../shared';
import Localization from '../../../smoothr-web-app-core/models/Localization';

interface SubCategory {
	identifier: string;
	name: Localization;
	articleCount: number;
	sortOrder: number;
}

@Component({
	selector: 'app-menu',
	templateUrl: './menu.page.html',
	styleUrls: ['menu.page.scss'],
	standalone: false,
})
export class MenuPage extends RepositoryDirective implements OnInit {
	static url = 'menu';

	@ViewChild('articleContent', { static: false }) articleList: any;
	@ViewChild('categoryList', { static: false }) categoryList: ElementRef;
	@ViewChild('drinkSubcategoryList', { static: false }) drinkSubcategoryList: ElementRef;
	@ViewChild('retailSubcategoryList', { static: false }) retailSubcategoryList: ElementRef;
	@ViewChild('promoSubcategoryList', { static: false }) promoSubcategoryList: ElementRef;

	environment = environment;
	scrollEvents = true;
	loading = false;
	categories: ArticleCategory[] = [];
	dayjs = dayjs;

	selectedToggleTag = TOGGLE_TAG.All;
	public showFilters = false;
	public selectedFiltersArray: string[] = [];
	showMessageNotification = false;
	public messageNotification = '';
	public searchQuery = '';

	// Consolidated State Maps for Subcategories
	public subCategoriesMap: Partial<Record<ArticleCategoryType, SubCategory[]>> = {};
	public selectedSubCategoryMap: Partial<Record<ArticleCategoryType, string | null>> = {};

	checkedMainTagCategories = false;
	mainTagCategories: any[] = [];
	preMainTagCategories = [
		{
			name: { en: 'Food', de: 'Essen' },
			tag: 'food',
			categoryType: ArticleCategoryType.DISH,
			categoryNameKeywords: [],
			icon: '../../../assets/topgolf/food-category-icon.svg',
		},
		{
			name: { en: 'Drinks', de: 'Getränke' },
			tag: 'drinks',
			categoryType: ArticleCategoryType.BEVERAGE,
			categoryNameKeywords: [],
			icon: '../../../assets/topgolf/drink-category-icon.svg',
		},
		{
			name: { en: 'Retail', de: 'Retail' },
			tag: 'retail',
			categoryType: ArticleCategoryType.OTHER,
			categoryNameKeywords: [],
			icon: '../../../assets/topgolf/retail.svg',
		},
		{
			name: { en: 'Promos', de: 'Promos' },
			tag: 'promos',
			categoryType: ArticleCategoryType.PROMOS,
			categoryNameKeywords: [],
			icon: '../../../assets/topgolf/retail.svg',
		},
	];

	public previousValue = 0;
	public hideCategory: boolean = false;
	orderTotalPrice = OrderUtils.orderTotalPrice;
	numberToCurrency = numberToCurrency;
	public selectedCategoryIndex$ = new BehaviorSubject<number>(0);

	protected readonly removeBasket = removeBasket;
	protected readonly addToBasket = addToBasket;
	protected readonly toggleBasket = toggleBasket;
	protected readonly ArticleCategoryType = ArticleCategoryType;

	private _selectedMainCategory = ArticleCategoryType.DISH;
	get selectedMainCategory(): ArticleCategoryType {
		return this._selectedMainCategory;
	}

	private _selectedCategoryIndex = 0;
	get selectedCategoryIndex(): number {
		return this._selectedCategoryIndex;
	}
	set selectedCategoryIndex(value) {
		if (this._selectedCategoryIndex === value) return;
		this._selectedCategoryIndex = value;
		this.scrollTo(value);
	}

	get selectedCategory(): ArticleCategory {
		return this.categories[this.selectedCategoryIndex];
	}

	get active(): boolean {
		return (
			this.order &&
			OrderUtils.articleGroupsTotalPrice(
				this.order.orderedArticles,
				this.order.type,
				this.order.preorder?.type,
				this.order.terminalorder?.type || null
			) > 0
		);
	}

	constructor(
		public translate: TranslateService,
		protected repository: RepositoryService,
		protected modalCtrl: ModalController,
		private snackbarCtrl: MatSnackBar,
		private navigationService: NavigationService,
		private router: Router,
		private route: ActivatedRoute,
		private navCtrl: NavController
	) {
		super(repository);
	}

	static async navigate(router: Router) {
		await router.navigateByUrl(MenuPage.url);
	}

	async ionViewWillEnter() {}

	async ngOnInit() {
		await super.ngOnInit();
		this.loading = true;

		if (!this.repository._venue) {
			this.repository.venue.emit(await this.repository.getVenue(this.order?.venue));
		}

		this.reloadCategories();
		this.loading = false;

		this.route.queryParams.subscribe(params => {
			if (params?.itemId) {
				const allArticles = this.venue.articleCategories.flatMap(c => c.articles);
				const article = allArticles.find(it => it.number === params.itemId);
				if (article) this.openModal(article, null, false);
			}
			if (params?.categoryId) {
				const categoryIndex = this.categories.findIndex(it => it._id === params.categoryId);
				if (categoryIndex !== -1) this.scrollTo(categoryIndex);
			}
		});
	}

	async scrollTo(index: number) {
		if (index < 0 || index >= this.categories.length) return;

		this.scrollEvents = false;
		if (this.selectedCategoryIndex !== index) {
			this.selectedCategoryIndex = index;
			await this.scrollCategoryListTo(index);
			await sleep(100);
			await this.scrollArticleListTo(index);
		}
		this.scrollEvents = true;
	}

	async onScroll(event: any) {
		const currentY = event?.detail?.currentY;
		this.hideCategory = currentY > this.previousValue;
		this.previousValue = currentY;

		if (!this.scrollEvents || !this.articleList) return;

		// Optimized scroll detection (Avoids mapping over all elements generating heavy reflows)
		const categoryElements = Array.from(this.articleList.el.children) as HTMLElement[];
		const categoryIndex = categoryElements.findIndex((el, index) => {
			const nextEl = categoryElements[index + 1];
			return currentY >= el.offsetTop && (!nextEl || currentY < nextEl.offsetTop);
		});

		if (categoryIndex !== -1 && categoryIndex !== this.selectedCategoryIndex) {
			this._selectedCategoryIndex = categoryIndex;
			this.selectedCategoryIndex$.next(categoryIndex);

			await this.scrollCategoryListTo(categoryIndex);
			await this.handleSubcategoryScroll(categoryIndex);
		}
	}

	async openModal(item: Article, category: ArticleCategory | null, addRecommendations = false, maxQuantity = 99) {
		if (this.showFilters) this.showFilters = false;
		if (this.loading) return;
		await openArticleEditModal(this.modalCtrl, this.repository, item, category, addRecommendations, maxQuantity);
	}

	reloadCategories() {
		if (!this.venue) {
			this.categories = [];
			return;
		}

		// 1. Load subcategories for all types dynamically
		[ArticleCategoryType.DISH, ArticleCategoryType.BEVERAGE, ArticleCategoryType.OTHER, ArticleCategoryType.PROMOS].forEach(
			type => (this.subCategoriesMap[type] = this.loadSubCategoriesForType(type))
		);

		const isSearchActive = !!this.searchQuery?.trim();

		// 2. Filter base categories.
		// When a search is active, ignore the selected main category and search globally across all categories.
		let baseCategories = this.venue.articleCategories
			.filter(c => !c.hidden && c.availability?.standard !== false)
			.filter(c => !c.availableHours || c.availableHours.length === 0 || TimeUtils.doesHoursMatchNow(c.availableHours))
			.filter(c => {
				if (!isSearchActive && this.selectedMainCategory && this.selectedMainCategory !== (ArticleCategoryType.ALL as any)) {
					const mainCatDef = this.preMainTagCategories.find(mc => mc.tag === this.selectedMainCategory);
					return mainCatDef ? c.mainCategory === mainCatDef.categoryType : true;
				}
				return true; // global search: include all categories
			});

		// 3. When searching globally, always use flat standard layout (no subcategory grouping).
		// Otherwise use existing subcategory structure for the selected main category.
		if (!isSearchActive) {
			const activeSubCats = this.subCategoriesMap[this.selectedMainCategory];
			if (this.selectedMainCategory !== ArticleCategoryType.DISH && activeSubCats?.length) {
				this.categories = this.createSubcategoryStructure(baseCategories, this.selectedMainCategory, activeSubCats);
			} else {
				this.categories = this.processStandardCategories(baseCategories);
			}
		} else {
			this.categories = this.processStandardCategories(baseCategories);
		}

		if (this.categories.length === 0 && isSearchActive) {
			this.snackbarCtrl.open(this.translate.instant('not_found_articles'), undefined, { duration: 3000 });
		}
	}

	private processStandardCategories(baseCategories: ArticleCategory[]): ArticleCategory[] {
		const query = this.searchQuery?.trim().toLowerCase();
		return baseCategories
			.map(category => {
				const cat = { ...category }; // Shallow copy instead of structuredClone for performance

				cat.articles = cat.articles.filter(article => {
					const isAvailable = getCustomAvailability(article, OrderType.STANDARD, null, null, false, false, true);
					if (!isAvailable) return false;

					// Search Query Filter
					if (query) {
						const nameDE = (article.name?.de || '').toLowerCase();
						const nameEN = (article.name?.en || '').toLowerCase();
						const descDE = (article.description?.de || '').toLowerCase();
						const descEN = (article.description?.en || '').toLowerCase();
						if (!nameDE.includes(query) && !nameEN.includes(query) && !descDE.includes(query) && !descEN.includes(query))
							return false;
					}

					// Toggle Tags
					if (this.selectedToggleTag !== TOGGLE_TAG.All && !this.passesToggleFilter(article)) return false;

					// Selected Array Filters
					if (this.selectedFiltersArray?.length > 0 && !this.passesArrayFilters(article)) return false;

					// Subcategory Filters (For Dish/Standard categories)
					const subCatId = this.selectedSubCategoryMap[cat.mainCategory];
					if (subCatId) {
						if (subCatId.startsWith('category_') && cat._id !== subCatId.replace('category_', '')) return false;
						if (!subCatId.startsWith('category_') && !article.tags?.some(tag => tag.identifier === subCatId)) return false;
					}
					return true;
				});
				return cat;
			})
			.filter(category => category.articles.length > 0);
	}

	onSearchChange(event: Event) {
		this.searchQuery = (event as CustomEvent).detail?.value ?? (event.target as HTMLInputElement).value ?? '';
		this.reloadCategories();
	}

	private passesToggleFilter(article: Article): boolean {
		const features = article.compound?.features;
		switch (this.selectedToggleTag) {
			case TOGGLE_TAG.Vegeterian:
				return !!features?.vegetarian;
			case TOGGLE_TAG.Vegan:
				return !!features?.vegan;
			case TOGGLE_TAG.Meat:
				return !!(features?.beef || features?.chicken || features?.pork);
			case TOGGLE_TAG.Fish:
				return !!features?.fish;
			case TOGGLE_TAG.Alcohol:
				return !article.compound?.additives?.containsAlcohol;
			default:
				return true;
		}
	}

	private passesArrayFilters(article: Article): boolean {
		return this.selectedFiltersArray.some(filter => {
			if (filter === 'vegan' && article.compound?.features?.['vegetarian']) return true;
			return !!article.compound?.features?.[filter];
		});
	}

	onVenue() {
		super.onVenue();
		if (!this.venue) {
			this.repository
				.getVenue(this.order?.venue)
				.then(venue => this.repository.venue.emit(venue))
				.catch(() => {
					this.reloadCategories();
					this.selectedCategoryIndex = 0;
				});
			return;
		}

		if (!this.checkedMainTagCategories) {
			this.mainTagCategories = this.preMainTagCategories.filter(mc => mc.tag === 'all' || this.isCategoriesInside(mc));
			if (this.mainTagCategories[0]?.tag) this._selectedMainCategory = this.mainTagCategories[0].tag;
			this.checkedMainTagCategories = true;
		}

		this.reloadCategories();
		this.selectedCategoryIndex = 0;
	}

	onVenueError(error: any) {
		super.onVenueError(error);
		this.snackbarCtrl.open(axiosErrorToMessage(this.translate, error));
		this.loading = false;
		this.snackbarCtrl.open(
			this.translate.instant('menu_page.venue_timed_out'),
			this.translate.instant('menu_page.venue_timed_out_action')
		);
	}

	async scrollArticleListTo(index: number, time = 0) {
		if (!this.articleList) return;
		await this.articleList.scrollToPoint(0, this.articleList.el.children[index]?.offsetTop + 10, time);
	}

	trackBy(index: number, item: Article) {
		return item._id;
	}
	trackByCategory(index: number, item: ArticleCategory) {
		return item._id;
	}

	goBack() {
		this.router.navigate([`/${this.repository._venue.readableId.split('_')[1]}/${this.repository._order.tableNumber}`]);
	}

	// Consolidated Subcategory Toggle
	async toggleSubCategory(type: ArticleCategoryType, identifier: string) {
		this.selectedSubCategoryMap[type] = identifier;
		const categoryIndex = this.categories.findIndex(cat => cat._id === `virtual_${identifier}`);
		if (categoryIndex >= 0) await this.scrollTo(categoryIndex);
	}

	selectMainCategory(tag: ArticleCategoryType) {
		this._selectedMainCategory = tag;
		const activeSubCats = this.subCategoriesMap[tag];

		// Preselect first subcategory natively if it exists
		if (activeSubCats?.length) {
			this.selectedSubCategoryMap[tag] = activeSubCats[0].identifier;
		}

		this.reloadCategories();
		this.scrollTo(0);
	}

	isCategoriesInside(mainCategory: any): boolean {
		if (!this.venue?.articleCategories) return true;
		if (mainCategory.tag === 'all') return true;

		return this.venue.articleCategories.some(category => !category.hidden && category.mainCategory === mainCategory.categoryType);
	}

	private async scrollCategoryListTo(index: number) {
		const el = this.categoryList?.nativeElement;
		if (index < 0 || !el?.children[index]) return;
		el.scrollTo(el.children[index].offsetLeft - 5, 0);
		await sleep(100);
	}

	private async scrollSubcategoryListTo(nativeElement: any, subCategories: any[], identifier: string) {
		if (!nativeElement) return;
		const subCatIndex = subCategories.findIndex(sc => sc.identifier === identifier);
		if (subCatIndex < 0 || !nativeElement.children[subCatIndex]) return;

		nativeElement.scrollTo(nativeElement.children[subCatIndex].offsetLeft - 5, 0);
		await sleep(100);
	}

	private createSubcategoryStructure(
		baseCategories: ArticleCategory[],
		categoryType: ArticleCategoryType,
		subcategories: any[]
	): ArticleCategory[] {
		const subcategoryGroups: ArticleCategory[] = [];
		const allValidArticles = baseCategories
			.filter(c => c.mainCategory === categoryType)
			.flatMap(c => c.articles)
			.filter(a => getCustomAvailability(a, OrderType.STANDARD, null, null, true, false, true));

		subcategories.forEach((subCat, index) => {
			let targetArticles: Article[] = [];

			if (subCat.identifier.startsWith('category_')) {
				const categoryId = subCat.identifier.replace('category_', '');
				const originalCategory = baseCategories.find(cat => cat._id === categoryId);
				if (originalCategory)
					targetArticles = originalCategory.articles.filter(a =>
						getCustomAvailability(a, OrderType.STANDARD, null, null, true, false, true)
					);
			} else {
				targetArticles = allValidArticles.filter(article => article.tags?.some(tag => tag.identifier === subCat.identifier));
			}

			if (targetArticles.length > 0) {
				const virtualCategory = new ArticleCategory();
				virtualCategory._id = `virtual_${subCat.identifier}`;
				virtualCategory.name = subCat.name;
				virtualCategory.mainCategory = categoryType;
				virtualCategory.articles = targetArticles;
				virtualCategory.visible = true;
				virtualCategory.hidden = false;
				virtualCategory.sortOrder = index;
				subcategoryGroups.push(virtualCategory);
			}
		});

		return subcategoryGroups;
	}

	private loadSubCategoriesForType(categoryType: ArticleCategoryType): any[] {
		if (!this.venue) return [];
		if (categoryType === ArticleCategoryType.PROMOS) {
			return this.venue.articleCategories
				.filter(c => c.mainCategory === categoryType && !c.hidden)
				.map(category => ({
					identifier: `category_${category._id}`,
					name: category.name,
					articleCount: category.articles.filter(a => getCustomAvailability(a, OrderType.STANDARD, null, null, true, false, true))
						.length,
					sortOrder: category.sortOrder || 0,
				}))
				.filter(cat => cat.articleCount > 0)
				.sort((a, b) => a.sortOrder - b.sortOrder);
		}

		const foundTags = new Map();
		const tagArticleCounts = new Map();

		this.venue.articleCategories
			.filter(c => c.mainCategory === categoryType)
			.forEach(category => {
				category.articles.forEach(article => {
					article.tags?.forEach(tag => {
						if (tag.identifier.startsWith('filter_')) {
							if (!foundTags.has(tag.identifier)) {
								foundTags.set(tag.identifier, { identifier: tag.identifier, name: tag.name });
								tagArticleCounts.set(tag.identifier, 0);
							}
							if (getCustomAvailability(article, OrderType.STANDARD, null, null, true, false, true)) {
								tagArticleCounts.set(tag.identifier, tagArticleCounts.get(tag.identifier) + 1);
							}
						}
					});
				});
			});

		let subcategories = Array.from(foundTags.values())
			.filter(tag => tagArticleCounts.get(tag.identifier) > 0)
			.sort((a, b) => a.identifier.localeCompare(b.identifier));

		if (subcategories.length === 0) {
			subcategories = this.venue.articleCategories
				.filter(c => c.mainCategory === categoryType && !c.hidden)
				.map(category => ({
					identifier: `category_${category._id}`,
					name: category.name,
					articleCount: category.articles.filter(a => getCustomAvailability(a, OrderType.STANDARD, null, null, true, false, true))
						.length,
					sortOrder: category.sortOrder || 0,
				}))
				.filter(cat => cat.articleCount > 0)
				.sort((a, b) => a.sortOrder - b.sortOrder);
		}

		return subcategories;
	}

	private async handleSubcategoryScroll(categoryIndex: number) {
		const categoryType = this.selectedMainCategory;
		const currentCategory = this.categories[categoryIndex];

		if (!currentCategory || !currentCategory._id.startsWith('virtual_')) return;

		const subCatIdentifier = currentCategory._id.replace('virtual_', '');
		const listMap = {
			[ArticleCategoryType.BEVERAGE]: this.drinkSubcategoryList,
			[ArticleCategoryType.OTHER]: this.retailSubcategoryList,
			[ArticleCategoryType.PROMOS]: this.promoSubcategoryList,
		};

		const nativeElement = listMap[categoryType as any]?.nativeElement;
		if (!nativeElement) return;

		if (this.selectedSubCategoryMap[categoryType] !== subCatIdentifier) {
			this.selectedSubCategoryMap[categoryType] = subCatIdentifier;
			await this.scrollSubcategoryListTo(nativeElement, this.subCategoriesMap[categoryType], subCatIdentifier);
		}
	}
}
